import SoftBadge from 'components/common/SoftBadge';
import { AuthPut } from 'helpers/api/AuthCRUD';
import { getPriorityTime } from 'helpers/calculateTime';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  FlowsterAIStudioLicence,
  FlowsterStudioReset,
  FSM_Licence,
  LicenceResponseAdmin,
  LicenceTypes,
} from 'types';
import { FlowsterStudioLicenceModel } from 'types/FlowsterStudioLicenceType';
import { RootState } from '../../../../../store/index';
import LicenceActionsDropdown from './LicenceActionsDropdown';
import { PrioritySelect } from './priority';
import { API } from 'helpers/api/endpoints';
import { isFlowsterAIStudio, isFlowsterStudio, isFSM, isJwtToken, isPasswordReset } from 'helpers/handleParseOfLincence';
interface deleteLicenceBody {
  licenceGuid: string;
  customerGuid: string;
}
interface Props {
  refetch: () => void;
}
async function deleteLicence(body: deleteLicenceBody, token: string) {
  if (token && body) {
    try {
      await AuthPut(API.licence.disable, body, token);
    } catch (error) {
      return null;
    }
  }
}
async function enableLicence(body: deleteLicenceBody, token: string) {
  if (token && body) {
    try {
      await AuthPut(API.licence.enable, body, token);
    } catch (error) {
      return null;
    }
  }
}
function downloadLicence(data: LicenceResponseAdmin) {
  // blob holds the base64-encoded original licence key file
  const file = new Blob([atob(data.blob)], { type: 'text/plain' });
  const blobUrl = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download =
    data.fileName || `${data.product.replaceAll(' ', '_')}_${data.id}.key`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}
export const licenceColumns = ({
  refetch,
}: Props): ColumnDef<LicenceResponseAdmin>[] => {
  const { t } = useTranslation('licenses');
  const token = useSelector((state: RootState) => state.token ?? '');
  const handleDelete = async (body: deleteLicenceBody, token: string) => {
    await deleteLicence(body, token);
    refetch();
  };
  const handleEnable = async (body: deleteLicenceBody, token: string) => {
    await enableLicence(body, token);
    refetch();
  };
  return [
    {
      header: t('Licence'),
      accessorKey: 'json',
      meta: {
        headerProps: {
          className: 'ps-2',
          style: { height: '46px', minWidth: '7rem' },
        },
        cellProps: {
          className: 'py-2 pe-4 text-900',
        },
      },
      cell: ({ row }) => {
        const { json, product } = row.original;
        const parsed: LicenceTypes = JSON.parse(json);

        if (parsed) {
          if (isFSM(parsed)) {
            return <>{product}</>;
          }
          if (isPasswordReset(parsed)) {
            return <>{product}</>;
          }
          if (isFlowsterStudio(parsed)) {
            const { Subscription } = parsed;
            console.log(product)
            return <>{`${product} ${Subscription.Type}`}</>;
          }
          if (isFlowsterAIStudio(parsed)) {
            const { planName } = parsed;
            return <>{`${product} ${planName}`}</>;
          }
        }

        return null;
      },
    },

    {
      header: t('Customer'),
      accessorKey: 'customer.name',
      meta: {
        headerProps: { style: { minWidth: '14.625rem' } },
        cellProps: {
          className: 'py-2 pe-4',
        },
      },

      cell: ({ row }) => {
        const { customer } = row.original;
        if (!customer) return null;
        const { name, id, guid } = customer;
        return (
          <Link to={`/admin/editcustomer/${guid}`} className="fw-semi-bold">
            {name}
          </Link>
        );
      },
    },
    {
      header: t('file_name'),
      accessorKey: 'fileName',
      meta: {
        cellProps: {
          className: 'py-2 pe-4',
        },
      },

      cell: ({ row }) => {
        const { fileName } = row.original;

        return (
          <div
            className="ellipsis-text
"
            title={fileName}
          >
            {fileName}
          </div>
        );
      },
    },

    {
      id: 'company',
      header: t('company'),
      meta: {
        cellProps: {
          className: 'py-2 pe-4 text-900',
        },
      },

      cell: ({ row }) => {
        const { json, product } = row.original;
        const parsed: LicenceTypes = JSON.parse(json);
        if ('Company' in parsed) {
          return (
            <div className="ellipsis-text" title={parsed.Company}>
              {parsed.Company}
            </div>
          );
        }
        if ('issuedTo' in parsed) {
          return (
            <div className="ellipsis-text" title={parsed.issuedTo}>
              {parsed.issuedTo}
            </div>
          );
        }
        return <></>;
      },
    },
    {
      id: 'product',
      header: t('product'),
      meta: {
        cellProps: {
          className: 'py-2 pe-4 text-900',
        },
      },

      cell: ({ row }) => {
        const { json, product } = row.original;
        const parsed: LicenceTypes = JSON.parse(json);
        if ('Product' in parsed) {
          return (
            <div className="ellipsis-text" title={parsed.Product}>
              {parsed.Product}
            </div>
          );
        }
        if ('planName' in parsed) {
          const plan =
            parsed.planTier != null
              ? `${parsed.planName} (Tier ${parsed.planTier})`
              : parsed.planName;
          return (
            <div className="ellipsis-text" title={plan}>
              {plan}
            </div>
          );
        }
        return <></>;
      },
    },
    {
      id: 'remaining',
      header: t('Remaining'),
      meta: {
        cellProps: {
          className: 'pe-4',
        },
      },
      cell: ({ row }) => {
        const { json, product } = row.original;
        const parsed: LicenceTypes = JSON.parse(json);
        if (parsed) {
          if (product === 'FSM') {
            const { Subscription, Maintenance } = parsed as FSM_Licence;
            const priorityTimeProps = getPriorityTime(Subscription);
            return <PrioritySelect {...priorityTimeProps} />;
          }
          if (product === 'Password Reset Portal') {
            const { Subscription } = parsed as FlowsterStudioReset;
            const priorityTimeProps = getPriorityTime(Subscription);
            return <PrioritySelect {...priorityTimeProps} />;
          }
          if (product === 'Flowster Studio') {
            const { Subscription } = parsed as FlowsterStudioLicenceModel;
            const priorityTimeProps = getPriorityTime(Subscription);
            return <PrioritySelect {...priorityTimeProps} />;
          }
          if (product === 'Flowster AI Studio') {
            const { validFrom, validUntil } = parsed as FlowsterAIStudioLicence;
            const priorityTimeProps = getPriorityTime({
              From: validFrom ?? '',
              Till: validUntil ?? null,
            });
            return <PrioritySelect {...priorityTimeProps} />;
          }
        }

        return null;
      },
    },

    {
      id: 'support',
      header: 'Support',
      meta: {
        cellProps: {
          className: 'pe-4',
        },
      },
      cell: ({ row }) => {
        const { json, product } = row.original;
        const parsed: LicenceTypes = JSON.parse(json);
        if (parsed) {
          if (product === 'FSM') {
            const { Subscription, Maintenance } = parsed as FSM_Licence;
            const priorityTimeProps = getPriorityTime(Maintenance);
            return <PrioritySelect {...priorityTimeProps} />;
          }
          if (product === 'Password Reset Portal') {
            const { Subscription, Support } = parsed as FlowsterStudioReset;
            const priorityTimeProps = getPriorityTime(Support);
            return <PrioritySelect {...priorityTimeProps} />;
          }
          if (product === 'Flowster Studio') {
            const { Subscription, Support } = parsed as FlowsterStudioReset;
            const priorityTimeProps = getPriorityTime(Support);
            return <PrioritySelect {...priorityTimeProps} />;
          }
          if (product === 'Flowster AI Studio') {
            // Support window = 30-day grace period after expiry
            const { validUntil, graceUntil } = parsed as FlowsterAIStudioLicence;
            const priorityTimeProps = getPriorityTime({
              From: validUntil ?? '',
              Till: graceUntil ?? null,
            });
            console.log(priorityTimeProps)
            return <PrioritySelect {...priorityTimeProps} />;
          }
        }

        return null;
      },
    },
    {
      accessorKey: 'deleted',
      header: t('Status') as string,
      cell: ({ row }) => {
        const isDeactivated = row.original.deleted;
        if (isDeactivated)
          return (
            <SoftBadge pill bg={'secondary'} className="me-2">
              {t('Inactive')}
            </SoftBadge>
          );
        return (
          <SoftBadge pill bg={'success'} className="me-2">
            {t('Active')}
          </SoftBadge>
        );
      },
    },
    {
      id: 'action',
      header: t('Action') as string,
      meta: {
        headerProps: {
          className: 'text-end table-sticky-end',
        },
        cellProps: {
          className: 'text-end table-sticky-end',
        },
      },
      enableSorting: false,
      cell: ({ row }) => {
        const data = row.original;
        const isDeactivated = data.deleted;
        let body: deleteLicenceBody;
        if (data.customer?.guid) {
          body = { customerGuid: data.customer?.guid, licenceGuid: data.guid };
        }

        return (
          <LicenceActionsDropdown
            canDownload={Boolean(data.blob)}
            canCopy={isJwtToken(data.encrypted)}
            isDeactivated={isDeactivated}
            onDownload={() => downloadLicence(data)}
            onCopyKey={() =>
              navigator.clipboard.writeText(data.encrypted.trim())
            }
            onActivate={() => handleEnable(body, token)}
            onDeactivate={() => handleDelete(body, token)}
          />
        );
      },
    },
  ];
};
